import { useFormHandler, useLoggedInAs } from "shared/hooks";
import { useCallback } from "react";
import { z } from "zod";
import {
  createMultilingual,
  getMultilingualString,
  isValidUrl,
  phoneNumberRegex,
} from "shared/utils";
import {
  EnumAddressLocationType,
  EnumEntityReferenceType,
  EnumOperatorEnrollmentsOnboardingStatus,
  EnumOperatorType,
  EnumOwnerType,
  EnumUserAccessOperatorsRole,
  EnumUserAccessOperatorsStatus,
  OperatorCreateMutationVariables,
  OperatorEnrollmentsInput,
  OperatorManyDirectoryDocument,
  OperatorManyDocument,
  OperatorsByUserDocument,
  RoleType,
  useOperatorCreateMutation,
  UserCreateInviteMutationVariables,
  UserFindOneQuery,
  useUserCreateInviteMutation,
  useUserUpdateMutation,
} from "gql/generated.ts";
import { i18nNamespace, loadLanguagePacks } from "i18n.ts";
import i18n from "i18next";
import { getNameFromUser } from "shared/utils/getNameFromUser";

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Core, i18nNamespace.Directory]);

const coordinateNumber = (label: string, min: number, max: number) =>
  z.preprocess(
    value => (value === "" || value === null ? undefined : Number(value)),
    z
      .number({
        required_error: `${label} must be a valid number`,
        invalid_type_error: `${label} must be a valid number`,
      })
      .min(min, `${label} must be between ${min} and ${max}`)
      .max(max, `${label} must be between ${min} and ${max}`),
  );

const addOperatorFormSchema = z
  .object({
    businessName: z
      .string()
      .min(
        1,
        `${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.BUSINESS_NAME")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      ),
    email: z
      .string()
      .min(
        1,
        `${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.PUBLIC_EMAIL_ADDRESS")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      )
      .email(
        `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.PUBLIC_EMAIL_ADDRESS")}`,
      )
      .optional()
      .or(z.literal("")),
    website: z
      .string()
      .refine(
        data => {
          if (data) {
            return isValidUrl(data);
          }
          return true;
        },
        `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.PUBLIC_WEBSITE")}`,
      )
      .optional()
      .or(z.literal("")),
    phone: z
      .string()
      .regex(
        phoneNumberRegex,
        `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.LOCAL_PUBLIC_PHONE")}`,
      )
      .optional()
      .or(z.literal("")),
    tollFreePhone: z
      .string()
      .regex(
        phoneNumberRegex,
        `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.TOLL_FREE_PUBLIC_PHONE")}`,
      )
      .optional()
      .or(z.literal("")),
    businessType: z
      .string()
      .min(
        1,
        `${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.BUSINESS_TYPE.TITLE")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      ),
    googleMapsPlaceId: z.string().optional(),
    fullAddress: z.string().optional(),
    fullAddressObj: z
      .object({
        line1: z.string().optional().default(""),
        line2: z.string().optional().default(""),
        city: z.string().optional().default(""),
        subdivision: z.string().optional().default(""),
        postalCode: z.string().optional().default(""),
        country: z.string().optional().default(""),
        location: z
          .object({
            type: z.string().default("Point"),
            coordinates: z.object({
              lat: coordinateNumber(
                i18n.t("core:SELECT_ADDRESS.LATITUDE"),
                -90,
                90,
              ),
              lng: coordinateNumber(
                i18n.t("core:SELECT_ADDRESS.LONGITUDE"),
                -180,
                180,
              ),
            }),
          })
          .optional(),
      })
      .partial()
      .optional(),
    invitedUser: z.object({
      email: z
        .string()
        .min(
          1,
          `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 2 })}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.INVITE_USER.EMAIL_ADDRESS")}`,
        )
        .email(
          `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.INVITE_USER.EMAIL_ADDRESS")}`,
        )
        .optional()
        .or(z.literal("")),
      firstName: z
        .string()
        .min(
          1,
          `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 1 })}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.INVITE_USER.FIRST_NAME")}`,
        )
        .optional()
        .or(z.literal("")),
      lastName: z
        .string()
        .min(
          1,
          `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 1 })}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.INVITE_USER.LAST_NAME")}`,
        )
        .optional()
        .or(z.literal("")),
      role: z.nativeEnum(EnumUserAccessOperatorsRole),
    }),
  })
  .superRefine((data, ctx) => {
    if (
      !!data.invitedUser.email ||
      !!data.invitedUser.firstName ||
      !!data.invitedUser.lastName
    ) {
      if (!data.invitedUser.email) {
        ctx.addIssue({
          message: `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 2 })}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.INVITE_USER.EMAIL_ADDRESS")}`,
          path: ["invitedUser.email"],
          code: "custom",
        });
      }
      if (!data.invitedUser.firstName) {
        ctx.addIssue({
          message: `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 1 })}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.INVITE_USER.FIRST_NAME")}`,
          path: ["invitedUser.firstName"],
          code: "custom",
        });
      }
      if (!data.invitedUser.lastName) {
        ctx.addIssue({
          message: `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 1 })}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.INVITE_USER.LAST_NAME")}`,
          path: ["invitedUser.lastName"],
          code: "custom",
        });
      }
    }
  });

export type AddOperatorFormValues = z.infer<typeof addOperatorFormSchema>;

interface AddOperatorFormOptions {
  onSuccess?: (
    operatorEntityid: string,
    operatorName: string,
    invitedUser?: string,
  ) => void;
  onError?: (message: string) => void;
  onClose?: () => void;
  // This can been used to pass in connected org ids or a single org id
  organizationIds?: string[];
  user?: UserFindOneQuery["userFindOne"];
  defaultBusinessName?: string;
}

export const useAddOperatorForm = ({
  onSuccess,
  onError,
  onClose,
  organizationIds,
  user,
  defaultBusinessName,
}: AddOperatorFormOptions) => {
  const defaultValues: AddOperatorFormValues = {
    businessName: defaultBusinessName ?? "",
    email: "",
    website: "",
    phone: "",
    tollFreePhone: "",
    businessType: "",
    googleMapsPlaceId: "",
    fullAddress: "",
    fullAddressObj: {
      line1: "",
      line2: "",
      city: "",
      subdivision: "",
      postalCode: "",
      country: "",
      location: {
        type: "Point",
        coordinates: {
          lat: 0,
          lng: 0,
        },
      },
    },
    invitedUser: {
      email: "",
      firstName: "",
      lastName: "",
      role: EnumUserAccessOperatorsRole.Owner,
    },
  };

  const formErrorMessage = (isInviteUserError?: boolean) =>
    isInviteUserError
      ? `${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.FORM_ERRORS.CREATE_USER_INVITE")} ${i18n.t("core:FORM_ERRORS.PLEASE_TRY_AGAIN")}`
      : `${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.FORM_ERRORS.CREATE_OPERATOR")} ${i18n.t("core:FORM_ERRORS.PLEASE_TRY_AGAIN")}`;

  const [createOperator] = useOperatorCreateMutation();
  const [createUserInvite] = useUserCreateInviteMutation();
  const [updateUser] = useUserUpdateMutation();

  const loggedInAs = useLoggedInAs();

  /**
   * Function to get enrollments for `createOperator` payload
   * If user exists:
   * 1. Check if accesses are present on user and construct enrollments based on those
   * 2. If accesses are not present, use `onboardingOrganization` from `onboardingMetadata`
   *
   * If organizationIds exists, construct enrollment with that or those ids
   */
  const getEnrollments = useCallback(
    (hasInvitedStaff: boolean) => {
      // When adding an operator from an organization, enrollments will ONLY be for that organization
      // Previously, it would add for ALL organizations the user has access to, but for TNB_INTERNAL users
      // with ALL organizations listed, this would add the operator to ALL organizations (not ideal)
      const isOrgUser = loggedInAs?.type === EnumOwnerType.Organization;
      const baseEnrollment = {
        onboardingStatus:
          EnumOperatorEnrollmentsOnboardingStatus.CompletedOnboarding,
        organizationControlled:
          // For DMO users, the operator is organization controlled IF there are no staff invited,
          // otherwise it is not organization controlled
          loggedInAs?.type === EnumOwnerType.Organization && !hasInvitedStaff
            ? true
            : false,
      };
      const enrollments: OperatorEnrollmentsInput[] = [];
      if (isOrgUser) {
        enrollments.push({
          ...baseEnrollment,
          organization: loggedInAs.entityId,
        });
      } else {
        // Add the enrollment from the user's onboarding organization
        if (user?.onboardingMetadata?.onboardingOrganization) {
          enrollments.push({
            ...baseEnrollment,
            organization: user.onboardingMetadata.onboardingOrganization,
          });
        } else if (organizationIds) {
          // Add the enrollments for the organizations passed in
          organizationIds.forEach(organizationId => {
            enrollments.push({
              ...baseEnrollment,
              organization: organizationId,
            });
          });
        }
      }
      return enrollments;
    },
    [organizationIds, user, loggedInAs],
  );

  return useFormHandler<AddOperatorFormValues>(
    useCallback(
      async data => {
        const isInvitingUser = !!(
          data?.invitedUser &&
          data?.invitedUser.email &&
          data?.invitedUser.firstName &&
          data?.invitedUser.lastName
        );
        const enrollments = getEnrollments(isInvitingUser);

        /**
         * Condition to check that we have sufficient data for `enrollments`
         * in the `createOperator` payload
         *
         * Will call `onError` if `user` was provided and there is no organizations
         * present on either the `onboardingMetadata` or in their `access` array, OR
         * if no `organizationId` was passed into the hook
         */
        if (enrollments.length == 0) {
          onError?.(
            i18n.t(
              "directory:OPERATOR.ADD_OPERATOR_MODAL.FORM_ERRORS.INVALID_ENROLLMENTS",
            ),
          );
          return;
        }

        if (data) {
          // If the fullAddressObj exists & line1 has a length greater than 0 -
          // based on the zod validation the rest of the address should be
          // filled out & will be accessible to use in the payload.
          const address =
            data.fullAddressObj &&
            data.fullAddressObj.line1 &&
            data.fullAddressObj.line1?.length > 0
              ? {
                  line1: data.fullAddressObj.line1 ?? "",
                  line2: data.fullAddressObj.line2 ?? "",
                  city: data.fullAddressObj.city ?? "",
                  subdivision: data.fullAddressObj.subdivision ?? "",
                  postalCode: data.fullAddressObj.postalCode ?? "",
                  country: data.fullAddressObj.country ?? "",
                  location: {
                    type:
                      (data.fullAddressObj.location
                        ?.type as EnumAddressLocationType) ?? "",
                    coordinates: [
                      data.fullAddressObj?.location?.coordinates?.lng ?? 0,
                      data.fullAddressObj?.location?.coordinates?.lat ?? 0,
                    ],
                  },
                }
              : undefined;
          const googleMyBusiness = data.googleMapsPlaceId
            ? `https://www.google.com/maps/place/?q=place_id:${data.googleMapsPlaceId}`
            : undefined;
          const links =
            data.website || googleMyBusiness
              ? {
                  ...(data.website ? { website: data.website } : {}),
                  ...(googleMyBusiness ? { googleMyBusiness } : {}),
                }
              : undefined;

          const operatorPayload: OperatorCreateMutationVariables = {
            record: {
              // DMO users set claimed to FALSE by default
              claimed:
                loggedInAs?.type === EnumOwnerType.Organization ? false : true,
              enrollments,
              name: createMultilingual(data.businessName ?? ""),
              type: data.businessType
                ? (data.businessType as EnumOperatorType)
                : EnumOperatorType.Accommodation,
              contact: {
                email:
                  data.email && data.email.length > 0 ? data.email : undefined,
                phone:
                  data.phone && data.phone?.length > 0 ? data.phone : undefined,
                tollFreePublicPhone:
                  data.tollFreePhone && data.tollFreePhone.length > 0
                    ? data.tollFreePhone
                    : undefined,
                address,
                links,
              },
            },
          };

          await createOperator({
            variables: operatorPayload,
            refetchQueries: [
              OperatorManyDocument,
              OperatorManyDirectoryDocument,
            ],
          })
            .then(async res => {
              let operatorEntityid: string = "";
              let operatorName: string = "";
              if (res.data && res.data?.operatorCreate?.record?.name) {
                operatorEntityid =
                  res.data?.operatorCreate?.record?._entityId ?? "";
                operatorName = getMultilingualString(
                  res.data.operatorCreate.record.name,
                );
              }
              if (isInvitingUser) {
                onClose?.();
                const inviteUserPayload: UserCreateInviteMutationVariables = {
                  email: data.invitedUser?.email ?? "",
                  firstName: data.invitedUser?.firstName ?? "",
                  lastName: data.invitedUser?.lastName ?? "",
                  // When adding a user while creating an operator, we'll assume
                  // that we want to send an invitation email
                  sendInvitationEmail: true,
                  entity: {
                    _entityId: res.data?.operatorCreate?.record?._entityId,
                    type: EnumEntityReferenceType.Operator,
                  },
                  role: RoleType.Owner,
                };
                await createUserInvite({
                  variables: inviteUserPayload,
                })
                  .then(res => {
                    const fullName = getNameFromUser(
                      res.data?.userCreateInvite,
                    );
                    onSuccess?.(operatorEntityid, operatorName, fullName);
                  })
                  .catch(() => {
                    onError?.(formErrorMessage(true));
                  });
              } else if (user) {
                /* Add the new operator to the user's `access` array */
                await updateUser({
                  variables: {
                    id: user._id,
                    record: {
                      access: {
                        ...user.access,
                        operators: [
                          ...user.access.operators,
                          {
                            entityId:
                              res.data?.operatorCreate?.record?._entityId,
                            role: EnumUserAccessOperatorsRole.Owner,
                            status: EnumUserAccessOperatorsStatus.Accepted,
                          },
                        ],
                      },
                    },
                  },
                  refetchQueries: [OperatorsByUserDocument],
                })
                  .then(() => onSuccess?.(operatorEntityid, operatorName))
                  .catch(() => {
                    onError?.(
                      i18n.t(
                        "directory:OPERATOR.ADD_OPERATOR_MODAL.FORM_ERRORS.UPDATE_USER_ACCESS",
                      ),
                    );
                  });
              } else {
                onSuccess?.(operatorEntityid, operatorName);
              }
            })
            .catch(() => {
              onError?.(formErrorMessage());
            });
        }
      },
      [
        createOperator,
        createUserInvite,
        getEnrollments,
        onClose,
        onError,
        onSuccess,
        updateUser,
        user,
        loggedInAs,
      ],
    ),
    defaultValues,
    { schema: addOperatorFormSchema },
  );
};
